import { HttpClient } from "@angular/common/http";
import { computed, inject, Injectable, Signal, signal } from "@angular/core";
import { rxResource } from "@angular/core/rxjs-interop";
import {
  AccessState,
  ApiResponse,
  Lesson,
  Module,
  ModuleStatus,
  ProgresItem,
} from "app/@core/types";
import { environment } from "environments/environment";
import { lastValueFrom, map, tap } from "rxjs";
import { Progress } from "../types";
import { unwrapResponse } from "../utils/api-utils";
import { DayjsService } from "../utils/dayjs.service";
import { CourseService } from "./course.service";
import { UserService } from "./users.service";

@Injectable({ providedIn: "root" })
export class ProgressService {
  private http = inject(HttpClient);
  private userService = inject(UserService);
  private courseService = inject(CourseService);
  private dayjs = inject(DayjsService);
  private baseUrl = environment.baseUrl + "courses/progress/";

  private readonly defaultProgress: Progress = {
    nextsteps: {},
    modules: {},
    masterclasses: {},
    lessons: {},
  };

  private _progress = signal<Progress>(this.defaultProgress);
  readonly progress = this._progress.asReadonly();
  readonly course = this.courseService.currentCourse;

  // New: Signal to track if any operation is currently hitting the API
  isLoaded = signal(false);

  readonly completedLessons = computed(() => {
    return Object.values(this._progress().lessons).filter(
      (l) => l.status === "Completed",
    ).length;
  });

  readonly progressResource = rxResource({
    // Define the dependency. Whenever activeCourseId() changes, loader runs.
    request: () => this.course()?.id,

    loader: ({ request: courseId }) => {
      if (!courseId || courseId == 0) return null;
      return this.http.get<ApiResponse>(`${this.baseUrl}${courseId}`).pipe(
        map(unwrapResponse<Progress>),
        tap((progress) => this._progress.set(progress)),
        tap(() => this.isLoaded.set(true)),
      );
    },
  });

  async load(cid: number): Promise<void> {
    try {
      const result = await lastValueFrom(
        this.http.get<ApiResponse>(this.baseUrl + cid),
      );
      if (result.status === "ok") {
        this.isLoaded.set(true);
        this._progress.set(result.data);
      }
    } catch (e) {
      console.error(e);
    }
  }

  unload(): void {
    this._progress.set(this.defaultProgress);
  }

  private isRoleBypassed(): boolean {
    return this.userService.isTestRole();
  }

  masterclassState = computed(() => {
    const course = this.course();
    const progress = this.progress();

    if (!course?.modules || !progress) return null;
    const masterclasses = course.masterclasses.filter((m) => m.is_pack === 1);
    return this.progressState(
      masterclasses,
      progress.masterclasses,
      course.course_startdate,
      course.options.progressby,
    );
  });

  moduleState = computed(() => {
    const course = this.course();
    const progress = this.progress();

    if (!course?.modules || !progress) return null;
    const modules = course.modules.filter((m) => m.is_pack === 0);
    return this.progressState(
      modules,
      progress.lessons,
      course.course_startdate,
      course.options.progressby,
    );
  });

  private progressState(
    modules: Module[],
    progress: { [key: string]: ProgresItem },
    courseStart: Date,
    progressBy: string,
    checkDep = true,
  ): Record<number, any> {
    const results: Record<number, ModuleStatus> = {};
    let previousModuleCompleted = true;

    modules.forEach((mod) => {
      let completedInModule = 0;
      const moduleLessons: Record<number, AccessState> = {};
      let lastLessonWasFinished = true; // Tracks chain within module

      // Module Level Lock Logic
      const isLockedByDate =
        mod.start_date &&
        this.dayjs.now().isBefore(this.dayjs.parseWithTZ(mod.start_date));
      const isLockedByDep = mod.is_dependent && !previousModuleCompleted;
      const isModuleLocked =
        !this.isRoleBypassed() && checkDep && (isLockedByDate || isLockedByDep);

      mod.lessons.forEach((lesson) => {
        // Extract status from the progress signal items
        const currentStatus = progress?.[lesson.id]?.status || "not-visited";

        // Execute corrected helper
        const access = this.calculateLessonAccess(
          lesson,
          courseStart,
          progressBy,
          isModuleLocked,
          lastLessonWasFinished,
          currentStatus,
        );

        moduleLessons[lesson.id] = access;

        // Update trackers for next lesson in loop
        if (currentStatus === "Completed") {
          completedInModule++;
          lastLessonWasFinished = true;
        } else {
          lastLessonWasFinished = false;
        }
      });

      const isDone =
        mod.lessons.length > 0 && completedInModule === mod.lessons.length;

      const total = mod.lessons.length;
      const percent =
        total > 0 ? Math.round((completedInModule / total) * 100) : 0;

      results[mod.id] = {
        isLocked: isModuleLocked,
        isCompleted: total > 0 && completedInModule === total,
        completedCount: completedInModule,
        totalLessons: total,
        progressPercent: percent,
        lessons: moduleLessons,
      };

      previousModuleCompleted = isDone;
    });

    return results;
  }

  private calculateLessonAccess(
    lesson: any,
    courseStartDate: Date,
    progressBy: string,
    isModuleLocked: boolean,
    isPrevLessonDone: boolean,
    currentStatus: string,
  ): AccessState {
    if (this.isRoleBypassed()) {
      return this.getAccessState(currentStatus, false);
    }
    let tooltip = "";

    if (isModuleLocked) {
      tooltip = "Please complete all the lessons of previous module";
      return {
        locked: true,
        status: "Locked",
        icon: "lock",
        color: "basic",
        tooltip,
      };
    }

    const today = this.dayjs.now();

    if (
      lesson.is_dependent &&
      typeof lesson.is_dependent === "number" &&
      progressBy === "day"
    ) {
      const unlockDate = this.dayjs
        .parseWithTZ(courseStartDate)
        .add(lesson.is_dependent, "days");
      if (today.isBefore(unlockDate)) {
        tooltip = `Available on ${unlockDate.format("MMM D, YYYY")}`;
        return {
          locked: true,
          status: "Locked",
          icon: "lock",
          color: "basic",
          tooltip,
        };
      }
    }

    if (
      lesson.start_date &&
      today.isBefore(this.dayjs.parseWithTZ(lesson.start_date))
    ) {
      const unlockDate = this.dayjs.parseWithTZ(lesson.start_date);
      tooltip = `Available on ${unlockDate.format("MMM D, YYYY")}`;
      return {
        locked: true,
        status: "Locked",
        icon: "lock",
        color: "basic",
        tooltip,
      };
    }

    if (lesson.is_dependent && !isPrevLessonDone) {
      tooltip = "Complete the previous lesson first.";
      return {
        locked: true,
        status: "Locked",
        icon: "lock",
        color: "basic",
        tooltip,
      };
    }

    return this.getAccessState(currentStatus, false);
  }

  private getAccessState(status: string, locked: boolean): AccessState {
    switch (status) {
      case "Completed":
        return {
          status,
          locked,
          icon: "checkmark-square-2",
          color: "success",
          tooltip: null,
        };
      case "Started":
        return {
          status,
          locked,
          icon: "square",
          color: "primary",
          tooltip: null,
        };
      default:
        return {
          status,
          locked,
          icon: "square",
          color: "basic",
          tooltip: null,
        };
    }
  }

  getModuleProgress(lessons: Lesson[]): Signal<number> {
    return computed(() => {
      const progress = this._progress().lessons;
      return lessons.filter((l) => progress[l.id]?.status === "Completed")
        .length;
    });
  }

  /**
   * Components can now use 'await' or just fire and forget
   */
  async markLessonStarted(cid: number, lid: number): Promise<boolean> {
    if (this._progress().lessons[lid]) return true;

    const lp = {
      id: lid,
      status: "Started",
      start_date: new Date().toISOString(),
      end_date: "",
    };

    try {
      await lastValueFrom(
        this.http.post<ApiResponse>(`${this.baseUrl}lessons`, {
          course_id: cid,
          lessons: lp,
        }),
      );
      this._progress.update((prev) => ({
        ...prev,
        lessons: { ...prev.lessons, [lid]: lp },
      }));
      return true;
    } catch {
      return false;
    }
  }

  async markLessonComplete(cid: number, lid: number): Promise<boolean> {
    const current = this._progress().lessons[lid];
    if (!current || current.status === "Completed") return true;

    const updatedLp = {
      ...current,
      status: "Completed",
      end_date: new Date().toISOString(),
    };

    try {
      await lastValueFrom(
        this.http.put<ApiResponse>(`${this.baseUrl}lessons`, {
          course_id: cid,
          lessons: updatedLp,
        }),
      );
      this._progress.update((prev) => ({
        ...prev,
        lessons: { ...prev.lessons, [lid]: updatedLp },
      }));
      return true;
    } catch {
      return false;
    }
  }

  async markMasterClassLessonStarted(
    cid: number,
    lid: number,
  ): Promise<boolean> {
    if (this._progress().masterclasses[lid]) return true;

    const mc = {
      id: lid,
      status: "Started",
      start_date: new Date().toISOString(),
      end_date: "",
    };

    try {
      await lastValueFrom(
        this.http.post<ApiResponse>(`${this.baseUrl}masterclasses`, {
          course_id: cid,
          masterclasses: mc,
        }),
      );
      this._progress.update((prev) => ({
        ...prev,
        masterclasses: { ...prev.masterclasses, [lid]: mc },
      }));
      return true;
    } catch {
      return false;
    }
  }

  async markMasterClassLessonComplete(
    cid: number,
    lid: number,
  ): Promise<boolean> {
    const current = this._progress().masterclasses[lid];
    if (!current || current.status === "Completed") return true;

    const updatedMc = {
      ...current,
      status: "Completed",
      end_date: new Date().toISOString(),
    };

    try {
      await lastValueFrom(
        this.http.put<ApiResponse>(`${this.baseUrl}masterclasses`, {
          course_id: cid,
          lessons: updatedMc,
        }),
      );
      this._progress.update((prev) => ({
        ...prev,
        masterclasses: { ...prev.masterclasses, [lid]: updatedMc },
      }));
      return true;
    } catch {
      return false;
    }
  }
}
